import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Stack } from '@mui/material';
import { CustomButton, Text } from '../../shared/uiComponents';
import { Download } from '../../shared/Assets/svgIcons';
import { UploadIcon } from '../../shared/Assets/svg';
import { TEXT } from '../../shared/Helpers/colors';
import { errorNormalizer } from '../../shared/Helpers/functions';
import { AdpRateMapping } from '../../redux/API/ClientAPIHelpers/adpRateMapping';
import { downloadFile } from '../Reports/helpers';
import { DispatchProperties } from '../../redux/store';
import { getAdpRateMappings } from '../../redux/State/clientSlice/adpRateMappingSlice';

const ImportMappings = ({ onClose }: { onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const inputRef = useRef<HTMLInputElement>(null);
	const [file, setFile] = useState<File>();
	const [importing, setImporting] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);
	const [summary, setSummary] = useState('');

	const downloadTemplate = () => {
		AdpRateMapping.downloadTemplate()
			.then((response) => {
				if (response?.error) throw response.error;
				downloadFile(response);
			})
			.catch(errorNormalizer);
	};

	const importFile = () => {
		if (!file) return;
		setImporting(true);
		setErrors([]);
		setSummary('');
		AdpRateMapping.importMappings(file)
			.then((response) => {
				if (response?.error) throw response.error;
				setErrors(response.errors || []);

				if (!response.errors?.length) {
					setSummary(`Import complete: ${response.created} mappings added, ${response.updated} updated.`);
					setFile(undefined);
					dispatch(getAdpRateMappings());
				}
			})
			.catch(errorNormalizer)
			.finally(() => setImporting(false));
	};

	return (
		<>
			<Text
				title="Add or update ADP Rate IDs for multiple providers using an Excel file."
				type="psm"
				color={TEXT.SECONDARY}
				className="marginBottom16"
			/>
			<ol style={{ paddingLeft: '20px', margin: '0 0 20px', lineHeight: '24px' }}>
				<li>Download the template.</li>
				<li>For each provider, enter a client or a service code and the matching ADP Rate ID.</li>
				<li>Choose the completed file, then select Import mappings.</li>
			</ol>
			<details className="marginBottom16">
				<summary style={{ cursor: 'pointer' }}>Template column guide</summary>
				<Text
					title="ProviderId: the provider’s Illuminate ID. Fill in either ClientId (the client’s Illuminate ID) or ServiceCode (an existing active code), not both. ADPRateId: the ADP Rate ID. Keep the template headers unchanged and preserve any leading zeros."
					type="psm"
					color={TEXT.SECONDARY}
					className="marginTop8"
				/>
			</details>
			<Stack direction="row" alignItems="center" gap="12px" flexWrap="wrap">
				<CustomButton
					title="Download template"
					buttonType="secondaryGray"
					startIcon={<Download />}
					onClick={downloadTemplate}
					disabled={importing}
				/>
				<CustomButton
					title="Choose Excel file"
					buttonType="secondary"
					startIcon={<UploadIcon />}
					onClick={() => inputRef.current?.click()}
					disabled={importing}
				/>
				<input
					ref={inputRef}
					type="file"
					accept=".xlsx"
					aria-label="Choose mappings file"
					hidden
					onChange={(event) => {
						setFile(event.target.files?.[0]);
						setErrors([]);
						setSummary('');
						event.target.value = '';
					}}
				/>
			</Stack>
			{file && <Text title={file.name} type="psm" className="marginTop16" style={{ overflowWrap: 'anywhere' }} />}
			{summary && (
				<Alert severity="success" role="status" sx={{ mt: 2 }}>
					{summary}
				</Alert>
			)}
			{errors.length > 0 && (
				<Alert severity="error" sx={{ mt: 2 }}>
					No mappings were imported. Correct the following items and choose the file again.
					<ul style={{ paddingLeft: '20px', marginBottom: 0 }}>
						{errors.map((error, index) => (
							<li key={index}>{error}</li>
						))}
					</ul>
				</Alert>
			)}
			<Stack direction="row" justifyContent="flex-end" alignItems="center" gap="12px" flexWrap="wrap" alignSelf="stretch" className="marginTop24">
				<CustomButton title="Close" buttonType="secondaryGray" onClick={onClose} disabled={importing} />
				<CustomButton title="Import mappings" onClick={importFile} loading={importing} disabled={!file} />
			</Stack>
		</>
	);
};

export default ImportMappings;
